import { computed, toValue, type MaybeRefOrGetter } from 'vue'

import { useAuth } from '@common/auth-layer/composables/useAuth'
import { type InfiniteData, type QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query'

import {
  GroupsService,
  type CreateGroupInput,
  type FormQuestionDTO,
  type GroupResponse,
  type MembershipQuestionsDTO,
} from '../services/groups.service'
import {
  type IGroup,
  type IMembershipQuestion,
  type IMembershipQuestions,
  type NewsletterConfigToPatchAPI,
  type IMembershipCustomFieldGroup,
  type UpdateGroupTabsConfigurationItem,
} from '../types/types'
import { invalidateCommunitiesMembershipCachesAfterLeaveGroup } from '../utils/invalidate-communities-membership-caches'
import { resetCommunitiesMembershipUiAfterLeaveGroup } from '../utils/reset-communities-membership-ui-after-leave-group'
import { isTerminalGroupsListPage } from '../utils/groups-list-pagination.utils'
import { invalidateTiersListAndJoinPlans } from '../utils/invalidate-tiers-and-plans.utils'
import { mergeGroupDetail } from '../utils/merge-group-detail.utils'
import { groupsQueryKeys } from '../utils/query-keys.utils'
import type { SyncSubscriptionResult } from '../types/group-tiers'

/** Read group from default groups list cache only (no search). Used as placeholder while groupBySlug fetches. */
function getGroupFromListCache(queryClient: QueryClient, groupSlug: string): IGroup | null {
  if (process.server) return null
  const data = queryClient.getQueryData(groupsQueryKeys.groups()) as InfiniteData<IGroup[]> | undefined
  if (!data?.pages) return null
  const flat = data.pages.flat()
  return flat.find((g: IGroup) => g.slug === groupSlug) ?? null
}

/**
 * Read an already fetched group slug from the groupById / groups list caches.
 * A location-scoped entry is preferred; the unscoped entry is a safe fallback because a group id
 * resolves to a single group regardless of which location context fetched it.
 */
function getGroupSlugFromCache(queryClient: QueryClient, groupId: string, locationId?: string): string | undefined {
  const cacheKeys = locationId
    ? [groupsQueryKeys.groupById(groupId, locationId), groupsQueryKeys.groupById(groupId)]
    : [groupsQueryKeys.groupById(groupId)]

  for (const cacheKey of cacheKeys) {
    const cachedGroup = queryClient.getQueryData(cacheKey) as GroupResponse | undefined
    if (cachedGroup?.groupDetail?.slug) return cachedGroup.groupDetail.slug
  }

  const listData = queryClient.getQueryData(groupsQueryKeys.groups()) as InfiniteData<IGroup[]> | undefined
  return listData?.pages?.flat().find((group: IGroup) => group._id === groupId)?.slug
}

interface UserGroupPreferenceCacheItem extends Record<string, unknown> {
  id: string
  groupId: string
  enabled: boolean
}

interface ToggleChatPreferenceMutationContext {
  previousQueries: [queryKey: readonly unknown[], groups: UserGroupPreferenceCacheItem[] | undefined][]
  locationId: string
}

const fromMembershipQuestionDTO = (question: FormQuestionDTO): IMembershipQuestion => {
  const customFieldId = question.customField?.id
  const customFieldKey = question.customField?.key

  return {
    question: question.question,
    answerType: question.answerType,
    answerOptions: question.answerOptions ?? [],
    ...(customFieldId && customFieldKey
      ? {
          customField: {
            id: customFieldId,
            key: customFieldKey,
          },
        }
      : {}),
  }
}

const toMembershipQuestionDTO = (question: IMembershipQuestion): FormQuestionDTO => {
  const customFieldId = question.customField?.id
  const customFieldKey = question.customField?.key

  return {
    answerType: question.answerType,
    question: question.question,
    answerOptions: question.answerOptions ?? [],
    ...(customFieldId && customFieldKey
      ? {
          customField: {
            id: customFieldId,
            key: customFieldKey,
          },
        }
      : {}),
  }
}

const normalizeMembershipQuestionsDTO = (data: MembershipQuestionsDTO): IMembershipQuestions => {
  return {
    ...data,
    questions: (data.questions ?? []).map(question => fromMembershipQuestionDTO(question)),
  }
}

/**
 * Groups Query Store - Handles fetching groups data using TanStack Query
 * Uses individual query creation functions for better scalability
 */
export const useGroupsQueryStore = () => {
  const service = new GroupsService()
  const queryClient = useQueryClient()
  const { store: authStore } = useAuth()

  // ===== QUERY KEY FACTORY =====
  const queryKeys = groupsQueryKeys

  // ===== QUERY CREATION FUNCTIONS (fetch* primary; create* aliases for backward compatibility) =====

  // Groups infinite query for pagination
  const fetchGroupsInfiniteQuery = (params: MaybeRefOrGetter<{ limit?: number; search?: string; enabled?: boolean }>) => {
    const infiniteQuery = useInfiniteQuery<IGroup[], Error>({
      queryKey: computed(() => {
        const p = toValue(params)
        return queryKeys.groups({ search: p?.search })
      }),
      queryFn: async ({ pageParam = 0 }) => {
        const p = toValue(params)
        const limit = p?.limit || 20
        const skip = (pageParam as number) * limit
        return service.getGroups({ ...p, limit, skip })
      },
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        const p = toValue(params)
        const limit = p?.limit || 20
        return isTerminalGroupsListPage(lastPage.length, limit) ? undefined : (lastPageParam as number) + 1
      },
      initialPageParam: 0,
      enabled: computed(() => {
        const p = toValue(params)
        return p?.enabled !== false && process.client
      }),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      refetchOnWindowFocus: false,
    })

    // Add computed allData property that flattens all pages
    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flat()
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  // Group by slug query: uses list cache as placeholder so UI can show partial data while API fetches.
  const fetchGroupBySlugQuery = (slug: MaybeRefOrGetter<string>) => {
    return useQuery<GroupResponse, Error>({
      queryKey: computed(() => {
        const s = toValue(slug)
        const key = queryKeys.groupBySlug(s || '')
        return key
      }),
      queryFn: async () => {
        const s = toValue(slug)
        if (!s) {
          throw new Error('Group slug is required for fetching group by slug')
        }

        const result = await service.getGroupBySlug(s)
        return result
      },
      placeholderData: (): GroupResponse | undefined => {
        const s = toValue(slug)
        if (!s) return undefined
        const listItem = getGroupFromListCache(queryClient, s)
        if (!listItem) return undefined
        return { groupDetail: listItem }
      },
      staleTime: 10 * 60 * 1000, // 10 minutes for group details
      gcTime: 15 * 60 * 1000, // 15 minutes
      enabled: computed(() => {
        const enabled = !!toValue(slug)
        return enabled
      }),
      refetchOnWindowFocus: false,
    })
  }

  // Group by ID query
  const fetchGroupByIdQuery = (groupId: MaybeRefOrGetter<string>) => {
    return useQuery<GroupResponse, Error>({
      queryKey: computed(() => {
        const id = toValue(groupId)
        return queryKeys.groupById(id || '')
      }),
      queryFn: async () => {
        const id = toValue(groupId)
        if (!id) {
          throw new Error('Group ID is required for fetching group by ID')
        }
        return service.getGroupById(id)
      },
      staleTime: 10 * 60 * 1000, // 10 minutes for group details
      gcTime: 15 * 60 * 1000, // 15 minutes
      enabled: computed(() => !!toValue(groupId)),
      refetchOnWindowFocus: false,
    })
  }

  /**
   * Imperative, cache-first slug lookup for a group id.
   * Content can be rendered outside its own group (user profile contributions, search),
   * so callers cannot rely on the routed group to build links.
   * @param locationId Location the content belongs to. Pass it whenever the content carries one:
   *   the group can live in a different location than the active one, and the service would
   *   otherwise scope the request to the active location.
   */
  const fetchGroupSlugById = async (groupId: string, locationId?: string): Promise<string | undefined> => {
    if (!groupId) return undefined

    const cachedSlug = getGroupSlugFromCache(queryClient, groupId, locationId)
    if (cachedSlug) return cachedSlug

    const response = await queryClient.fetchQuery<GroupResponse, Error>({
      queryKey: queryKeys.groupById(groupId, locationId),
      queryFn: () => service.getGroupById(groupId, locationId),
      staleTime: 10 * 60 * 1000,
      gcTime: 15 * 60 * 1000,
    })

    return response?.groupDetail?.slug
  }

  // Pinned groups query (only when logged in)
  const fetchPinnedGroupsQuery = () => {
    return useQuery<IGroup[], Error>({
      queryKey: queryKeys.pinnedGroups(),
      queryFn: () => service.getPinnedGroups(),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      enabled: computed(() => process.client && authStore.isLoggedIn),
      refetchOnWindowFocus: false,
    })
  }

  // Pin group mutation
  const pinGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      return service.pinGroup(groupId)
    },
    onMutate: async (groupId: string) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: queryKeys.pinnedGroups() })

      // Get current data
      const previousPinnedGroups = queryClient.getQueryData(queryKeys.pinnedGroups())
      const allGroups = queryClient.getQueryData(queryKeys.all) as { pages?: IGroup[][] } | undefined
      const groupToPin = allGroups?.pages?.flat().find((group: IGroup) => group._id === groupId)

      // Optimistically add to pinned groups
      if (groupToPin) {
        queryClient.setQueryData(queryKeys.pinnedGroups(), (old: IGroup[] | undefined) => {
          const pinnedGroups = old || []
          if (pinnedGroups.some((group: IGroup) => group._id === groupId)) return pinnedGroups
          return [groupToPin, ...pinnedGroups]
        })
      }

      return { previousPinnedGroups }
    },
    onError: (err, groupId, context) => {
      // Revert on error
      if (context?.previousPinnedGroups) {
        queryClient.setQueryData(queryKeys.pinnedGroups(), context.previousPinnedGroups)
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: queryKeys.pinnedGroups() })
    },
  })

  // Unpin group mutation
  const unpinGroupMutation = useMutation({
    mutationFn: async (groupId: string) => {
      return service.unpinGroup(groupId)
    },
    onMutate: async (groupId: string) => {
      // Cancel outgoing queries
      await queryClient.cancelQueries({ queryKey: queryKeys.pinnedGroups() })

      // Get current data
      const previousPinnedGroups = queryClient.getQueryData(queryKeys.pinnedGroups())

      // Optimistically remove from pinned groups
      queryClient.setQueryData(queryKeys.pinnedGroups(), (old: IGroup[] | undefined) => {
        const pinnedGroups = old || []
        return pinnedGroups.filter((group: IGroup) => group._id !== groupId)
      })

      return { previousPinnedGroups }
    },
    onError: (err, groupId, context) => {
      // Revert on error
      if (context?.previousPinnedGroups) {
        queryClient.setQueryData(queryKeys.pinnedGroups(), context.previousPinnedGroups)
      }
    },
    onSettled: () => {
      // Refetch to sync with server
      queryClient.invalidateQueries({ queryKey: queryKeys.pinnedGroups() })
    },
  })

  // Sync pricing mutation
  const syncPricingMutation = useMutation<unknown, Error, string>({
    mutationFn: async (groupId: string) => {
      return service.syncPricing(groupId)
    },
    onSuccess: () => {
      // Invalidate group details to refresh pricing information
      queryClient.invalidateQueries({ queryKey: queryKeys.groupBySlug('') })
    },
  })

  // Sync legacy PAID groups onto a Default subscription tier (idempotent)
  const syncSubscriptionMutation = useMutation<SyncSubscriptionResult, Error, { groupId: string; locationId: string }>({
    mutationFn: async ({ groupId, locationId }) => {
      return service.syncSubscription(groupId, locationId)
    },
    onSuccess: (_data, { groupId, locationId }) => {
      // Sync may create a Default tier — refresh admin list AND member join catalogue.
      invalidateTiersListAndJoinPlans(queryClient, groupId, locationId)
    },
  })

  // Mark group free (deactivate tiers + optional subscription cancel)
  const markGroupFreeMutation = useMutation<IGroup, Error, { groupId: string; locationId: string; cancelSubscriptions?: boolean }>({
    mutationFn: async ({ groupId, locationId, cancelSubscriptions = false }) => {
      return service.markGroupFree(groupId, locationId, cancelSubscriptions)
    },
    onSuccess: (updatedGroup, { groupId, locationId }) => {
      invalidateTiersListAndJoinPlans(queryClient, groupId, locationId)

      // Refresh slug/id group caches so membership type reflects FREE.
      // Deep-merge nested settings/branding so a lean mark-free payload cannot wipe them.
      const queryCache = queryClient.getQueryCache()
      queryCache.getAll().forEach(query => {
        const queryKey = query.queryKey as string[]
        if (queryKey[0] !== 'groups' || (queryKey[1] !== 'slug' && queryKey[1] !== 'id')) return

        const cachedData = query.state.data as GroupResponse | undefined
        if (cachedData?.groupDetail?._id !== groupId) return

        queryClient.setQueryData(queryKey, {
          ...cachedData,
          groupDetail: mergeGroupDetail(cachedData.groupDetail, updatedGroup),
        })
      })
    },
  })

  // Membership questions form query
  const fetchMembershipQuestionsFormQuery = (
    groupId: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string>,
    formId: MaybeRefOrGetter<string>
  ) => {
    return useQuery({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const fId = toValue(formId)
        return queryKeys.membershipForm(gId, fId, lId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const fId = toValue(formId)
        if (!gId || !lId || !fId) {
          throw new Error('Group ID, Location ID, and Form ID are required')
        }
        const data = await service.fetchMembershipQuestionsForm(gId, lId, fId)
        return normalizeMembershipQuestionsDTO(data)
      },
      staleTime: 10 * 60 * 1000, // 10 minutes - forms don't change often
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        const fId = toValue(formId)
        return !!gId && !!lId && !!fId
      }),
    })
  }

  const fetchMembershipCustomFieldsQuery = (
    groupId: MaybeRefOrGetter<string>,
    locationId: MaybeRefOrGetter<string>,
    enabled: MaybeRefOrGetter<boolean> = true
  ) => {
    return useQuery<IMembershipCustomFieldGroup[], Error>({
      queryKey: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        return queryKeys.membershipCustomFields(gId, lId)
      }),
      queryFn: async () => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)

        if (!gId || !lId) {
          throw new Error('Group ID and Location ID are required')
        }

        return service.fetchMembershipCustomFields(gId, lId)
      },
      staleTime: 10 * 60 * 1000,
      enabled: computed(() => {
        const gId = toValue(groupId)
        const lId = toValue(locationId)
        return !!gId && !!lId && toValue(enabled) !== false
      }),
      refetchOnWindowFocus: false,
    })
  }

  // Discovery groups infinite query (discoverable/public groups, paginated via skip/limit)
  const fetchDiscoveryGroupsQuery = (locationId: MaybeRefOrGetter<string | undefined>, params?: MaybeRefOrGetter<{ limit?: number }>) => {
    const infiniteQuery = useInfiniteQuery<IGroup[], Error>({
      queryKey: computed(() => {
        const lId = toValue(locationId)
        const p = toValue(params)
        return queryKeys.discovery(lId, p)
      }),
      queryFn: async ({ pageParam = 0 }) => {
        const lId = toValue(locationId)
        const p = toValue(params)
        if (!lId) {
          throw new Error('Location ID is required for fetching discovery groups')
        }
        const limit = p?.limit ?? 10
        const skip = (pageParam as number) * limit
        return service.getDiscoveryGroups(lId, { skip, limit })
      },
      getNextPageParam: (lastPage, allPages, lastPageParam) => {
        const p = toValue(params)
        const limit = p?.limit ?? 10
        return lastPage.length < limit ? undefined : (lastPageParam as number) + 1
      },
      initialPageParam: 0,
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
      enabled: computed(() => {
        const lId = toValue(locationId)
        return !!lId && authStore.isLoggedIn
      }),
      refetchOnWindowFocus: false,
    })

    const allData = computed(() => {
      if (!infiniteQuery.data?.value?.pages) return []
      return infiniteQuery.data.value.pages.flat()
    })

    return {
      ...infiniteQuery,
      allData,
    }
  }

  // User groups query (for chat settings)
  const fetchUserGroupsQuery = (
    locationId: MaybeRefOrGetter<string | null>,
    params?: MaybeRefOrGetter<{
      contactId?: string
      sort?: string
      sortKey?: string
      limit?: number
      status?: string
    }>
  ) => {
    return useQuery<
      {
        id: string
        name: string
        enabled: boolean
        avatar: string
        groupId: string
        locationId: string
        role: string
        memberCount: number
        slug: string
        branding: {
          logo: string
          cover: string
        }
        description?: string
        joinedAt?: string
      }[],
      Error
    >({
      queryKey: computed(() => {
        const lId = toValue(locationId)
        const p = toValue(params)
        return [...queryKeys.userGroups(lId || undefined), p] as const
      }),
      queryFn: async () => {
        const lId = toValue(locationId)
        const p = toValue(params)
        if (!lId) {
          throw new Error('Location ID is required for fetching user groups')
        }
        const response = await service.getUserGroups(lId, p)
        // Transform API response to match expected format
        const groups = (response.groups || response || []).map((group: any) => ({
          id: group._id || group.id,
          name: group.name || group.title,
          enabled: !group.isChatDisabled,
          avatar: group.avatar || group.image,
          groupId: group.groupId || group._id || group.id,
          locationId: group.locationId,
          role: group.role,
          memberCount: group.memberCount || 0,
          slug: group.slug,
          branding: {
            logo: group.branding?.logo,
            cover: group.branding?.cover,
          },
          description: String(group.description ?? group.about ?? '').trim() || undefined,
          joinedAt: group?.joinedAt ?? undefined,
        }))
        return groups
      },
      staleTime: 5 * 60 * 1000, // 5 minutes
      enabled: computed(() => !!toValue(locationId)),
    })
  }

  // Toggle chat preference mutation
  const userGroupsQueryPrefix = ['groups', 'user'] as const
  interface ToggleChatPreferenceMutationContext {
    previousQueries: [readonly unknown[], any[] | undefined][]
    locationId: string
  }
  const toggleChatPreferenceMutation = useMutation<
    { isChatDisabled?: boolean } | undefined,
    Error,
    { locationId: string; groupId: string; chatEnabled: boolean },
    ToggleChatPreferenceMutationContext
  >({
    mutationFn: async ({ locationId, groupId, chatEnabled }) => {
      return await service.toggleChatPreference(groupId, { chatEnabled }, locationId)
    },
    onMutate: async ({ locationId, groupId, chatEnabled }) => {
      // Cancel outgoing refetches for all user-groups queries.
      await queryClient.cancelQueries({ queryKey: userGroupsQueryPrefix })

      // Capture and optimistically update all matching user-groups queries
      const previousQueries = queryClient.getQueriesData<UserGroupPreferenceCacheItem[]>({
        queryKey: userGroupsQueryPrefix,
      })

      previousQueries.forEach(([queryKey, groups]) => {
        if (!groups) return
        const updatedGroups = groups.map(group =>
          group.groupId === groupId || group.id === groupId ? { ...group, enabled: chatEnabled } : group
        )
        queryClient.setQueryData(queryKey, updatedGroups)
      })

      return { previousQueries, locationId }
    },
    onError: (err, variables, context) => {
      // Revert on error
      if (context?.previousQueries?.length) {
        context.previousQueries.forEach(([queryKey, groups]) => {
          queryClient.setQueryData(queryKey, groups)
        })
      }
      console.error('Failed to toggle group preference:', err)
    },
    onSuccess: (data, variables) => {
      // Do not invalidate immediately: /groups endpoint can be eventually consistent.
      // Keep UI aligned with mutation response (or requested value).
      const resolvedEnabled = typeof data?.isChatDisabled === 'boolean' ? !data.isChatDisabled : variables.chatEnabled
      const matchingQueries = queryClient.getQueriesData<UserGroupPreferenceCacheItem[]>({
        queryKey: userGroupsQueryPrefix,
      })
      matchingQueries.forEach(([queryKey, groups]) => {
        if (!groups) return
        const updatedGroups = groups.map(group =>
          group.groupId === variables.groupId || group.id === variables.groupId ? { ...group, enabled: resolvedEnabled } : group
        )
        queryClient.setQueryData(queryKey, updatedGroups)
      })
    },
  })

  // Update group mutation
  const updateGroupMutation = useMutation<
    IGroup,
    Error,
    { groupId: string; updates: Partial<CreateGroupInput>; locationId: string },
    { previousData?: GroupResponse }
  >({
    mutationFn: async ({ groupId, updates, locationId }) => {
      return service.updateGroup(groupId, updates, locationId)
    },
    onMutate: async ({ groupId, updates }) => {
      // Cancel any outgoing refetches to prevent race conditions
      await queryClient.cancelQueries({ queryKey: ['groups', 'slug'] })

      // Find and update only the group by slug query
      const queryCache = queryClient.getQueryCache()
      const queries = queryCache.getAll()
      let previousData: GroupResponse | undefined

      queries.forEach(query => {
        const queryKey = query.queryKey as string[]

        // Update only group by slug queries
        if (queryKey[0] === 'groups' && queryKey[1] === 'slug') {
          const cachedData = query.state.data as GroupResponse | undefined
          if (cachedData?.groupDetail?._id === groupId) {
            previousData = cachedData

            queryClient.setQueryData(queryKey, {
              ...cachedData,
              groupDetail: {
                ...cachedData.groupDetail,
                ...updates,
              },
            })
          }
        }
      })

      return { previousData }
    },
    onError: (error, { groupId, updates }, context) => {
      console.error('[GroupsQueryStore.updateGroupMutation] Mutation failed:', {
        error,
        groupId,
        updates,
      })

      // Revert optimistic updates
      if (context?.previousData) {
        const queryCache = queryClient.getQueryCache()
        const queries = queryCache.getAll()

        queries.forEach(query => {
          const queryKey = query.queryKey as string[]
          if (queryKey[0] === 'groups' && queryKey[1] === 'slug') {
            const cachedData = query.state.data as GroupResponse | undefined
            if (cachedData?.groupDetail?._id === groupId) {
              queryClient.setQueryData(queryKey, context.previousData)
            }
          }
        })
      }
    },
  })

  const updateGroupTabsConfigurationMutation = useMutation<
    IGroup,
    Error,
    { groupId: string; locationId: string; groupSlug: string; tabsConfiguration: UpdateGroupTabsConfigurationItem[] },
    { previousData: GroupResponse | undefined }
  >({
    mutationFn: async ({ groupId, locationId, tabsConfiguration }) => {
      return service.updateGroupTabsConfiguration(groupId, locationId, tabsConfiguration)
    },
    onMutate: async ({ groupSlug, tabsConfiguration }) => {
      const queryKey = queryKeys.groupBySlug(groupSlug)
      await queryClient.cancelQueries({ queryKey })

      const previousData = queryClient.getQueryData<GroupResponse>(queryKey)

      if (previousData?.groupDetail) {
        queryClient.setQueryData(queryKey, {
          ...previousData,
          groupDetail: {
            ...previousData.groupDetail,
            settings: {
              ...previousData.groupDetail.settings,
              tabsConfiguration,
            },
          },
        })
      }

      return { previousData }
    },
    onError: (_error, { groupSlug }, context) => {
      if (context?.previousData) {
        queryClient.setQueryData(queryKeys.groupBySlug(groupSlug), context.previousData)
      }
    },
    onSuccess: (_data, { groupSlug }) => {
      queryClient.invalidateQueries({ queryKey: queryKeys.groupBySlug(groupSlug) })
    },
  })

  // Leave group mutation
  const leaveGroupMutation = useMutation<void, Error, { groupId: string; locationId?: string }>({
    mutationFn: async ({ groupId, locationId }) => {
      return service.leaveGroup(groupId, locationId)
    },
    onSuccess: (_data, { groupId, locationId }) => {
      invalidateCommunitiesMembershipCachesAfterLeaveGroup(queryClient, { groupId, locationId })
      resetCommunitiesMembershipUiAfterLeaveGroup()
    },
  })

  // Upsert membership questions form mutation
  const upsertMembershipQuestionsFormMutation = useMutation<
    IMembershipQuestions,
    Error,
    { groupId: string; locationId: string; questions: IMembershipQuestion[] }
  >({
    mutationFn: async ({ groupId, locationId, questions }) => {
      const payloadQuestions = questions.map(question => toMembershipQuestionDTO(question))
      const data = await service.upsertMembershipQuestionsForm(groupId, locationId, payloadQuestions)
      return normalizeMembershipQuestionsDTO(data)
    },
    onSuccess: (data, { groupId, locationId }) => {
      // Write the returned form directly into the form cache — no refetch needed
      queryClient.setQueryData(queryKeys.membershipForm(groupId, data._id, locationId), data)

      // Also update formId in any cached group-by-slug entry for this group
      const queryCache = queryClient.getQueryCache()
      queryCache.getAll().forEach(query => {
        const key = query.queryKey as string[]
        if (key[0] === 'groups' && key[1] === 'slug') {
          const cached = query.state.data as GroupResponse | undefined
          if (cached?.groupDetail?._id === groupId) {
            queryClient.setQueryData(key, {
              ...cached,
              membershipQuestions: data,
              groupDetail: {
                ...cached.groupDetail,
                settings: {
                  ...cached.groupDetail.settings,
                  formId: data._id,
                },
              },
            })
          }
        }
      })
    },
  })

  const updateNewsletterConfigMutation = useMutation<
    IGroup,
    Error,
    { groupId: string; locationId: string; config: NewsletterConfigToPatchAPI }
  >({
    mutationFn: async ({ groupId, locationId, config }) => {
      return service.updateNewsletterConfig(groupId, locationId, config)
    },
    onSuccess: (updatedGroup, { groupId }) => {
      const queryCache = queryClient.getQueryCache()
      queryCache.getAll().forEach(query => {
        const key = query.queryKey as string[]
        if (key[0] === 'groups' && key[1] === 'slug') {
          const cached = query.state.data as GroupResponse | undefined
          if (cached?.groupDetail?._id === groupId) {
            queryClient.setQueryData(key, {
              ...cached,
              groupDetail: updatedGroup,
            })
          }
        }
      })
    },
  })

  return {
    // Query creation functions (fetch* primary)
    fetchGroupsInfiniteQuery,
    fetchGroupBySlugQuery,
    fetchGroupByIdQuery,
    fetchGroupSlugById,
    fetchPinnedGroupsQuery,
    fetchDiscoveryGroupsQuery,
    fetchMembershipQuestionsFormQuery,
    fetchMembershipCustomFieldsQuery,
    fetchUserGroupsQuery,

    // Mutations
    pinGroupMutation,
    unpinGroupMutation,
    leaveGroupMutation,
    syncPricingMutation,
    syncSubscriptionMutation,
    markGroupFreeMutation,
    updateGroupMutation,
    updateGroupTabsConfigurationMutation,
    updateNewsletterConfigMutation,
    toggleChatPreferenceMutation,
    upsertMembershipQuestionsFormMutation,

    // Query keys for manual invalidation
    queryKeys,
  }
}
